import {
  DiscoveryApi,
  FetchApi,
  createApiRef,
} from '@backstage/core-plugin-api';
import {
  ChartSummaryResponse,
  ApplicationsResponse,
  ApplicationsQueryParams,
  ApplicationResponse,
  ChartSummaryQuery,
  BaseApplicationQueryParams,
} from '@internal/plugin-dev-services-chart-summary-common';

const BACKEND_PLUGIN_NAME = 'dev-services-chart-summary';

export const chartSummaryApiRef = createApiRef<ChartSummaryApi>({
  id: 'plugin.dev-services-chart-summary.api',
});

class FetchError extends Error {
  constructor(
    message: string,
    private readonly error_status: number,
  ) {
    super(message);
  }

  get status(): number {
    return this.error_status;
  }

  get name(): string {
    return this.constructor.name;
  }

  static async forResponse(resp: Response): Promise<FetchError> {
    return new FetchError(
      `Request failed with status code ${
        resp.status
      }.\nReason: ${await resp.text()}`,
      resp.status,
    );
  }
}

export type ChartSummaryApi = {
  getApplication(
    applicationId: string,
    query?: BaseApplicationQueryParams,
  ): Promise<ApplicationResponse>;
  getApplications(
    query?: ApplicationsQueryParams,
  ): Promise<ApplicationsResponse>;
  getChartSummary(query: ChartSummaryQuery): Promise<ChartSummaryResponse>;
};

export class ChartSummaryHttpApi implements ChartSummaryApi {
  static create(discoveryApi: DiscoveryApi, fetchApi: FetchApi) {
    return new ChartSummaryHttpApi(discoveryApi, fetchApi);
  }

  constructor(
    private discoveryApi: DiscoveryApi,
    private fetchApi: FetchApi,
  ) {}

  async getApplications(
    query?: ApplicationsQueryParams,
  ): Promise<ApplicationsResponse> {
    let endpoint = 'applications';

    if (query) {
      endpoint = endpoint.concat(
        `?${new URLSearchParams(query as any).toString()}`,
      );
    }

    return this.fetchGetEndpoint(endpoint);
  }

  async getChartSummary(
    query: ChartSummaryQuery,
  ): Promise<ChartSummaryResponse> {
    return this.fetchGetEndpoint(
      `application/chart-summary?${new URLSearchParams(
        query as any,
      ).toString()}`,
    );
  }

  async getApplication(
    applicationId: string,
    query?: BaseApplicationQueryParams,
  ): Promise<ApplicationResponse> {
    let endpoint = `applications/${applicationId}`;

    if (query) {
      endpoint = endpoint.concat(
        `?${new URLSearchParams(query as any).toString()}`,
      );
    }

    return this.fetchGetEndpoint(endpoint);
  }

  private async fetchGetEndpoint(endpoint: string) {
    const apiUrl = await this.discoveryApi.getBaseUrl(BACKEND_PLUGIN_NAME);
    const resp = await this.fetchApi.fetch(`${apiUrl}/${endpoint}`);
    if (!resp.ok) throw await FetchError.forResponse(resp);
    return await resp.json();
  }
}
